import {
  MonitorIllustration,
  PaperPlaneIllustration,
} from '@carboncopyco/illustrations';
import { Button } from '@carboncopyco/ui';
import { useEffect } from 'react';
import { AppEventsEnum } from '../../../../shared/enums/AppEventsEnum';
import { APP_MODAL_Z_INDEX } from '../../../constants';
import AppVersionModal from '../../../modals/AppVersionModal/AppVersionModal';
import RestartAppModal from '../../../modals/RestartAppModal/RestartAppModal';
import DiagnosticCheckScreen from '../../DiagnosticCheck/DiagnosticCheckScreen';
import ModalWrapper from '../ModalWrapper';
import { ModalType, useModals } from '../useModals';

const GlobalAppModals = () => {
  const {
    handleSetModalType,
    modalData,
    setModalData,
    modalType,
    closeModal,
    forceModal,
    setForceModal,
    modalActions,
    modalRef,
    modalConfigByType,
  } = useModals();

  const showAboutUs = (appData: any) => {
    const { appVersion } = appData;
    if (!appVersion) return;
    handleSetModalType(ModalType.APP_VERSION);
    setModalData({
      appVersion,
      showInfo: true,
    });
  };

  const diagnosticCheck = (_args: any) => {
    setForceModal(true);
    handleSetModalType(ModalType.DIAGNOSTICS_CHECK);
    setModalData({
      appData: _args,
    });
  };

  const showRestartAppModal = () => {
    handleSetModalType(ModalType.RESTART_APP);
  };

  const showUpdateApp = ({
    appVersion,
    onSubmit,
    updateAvailable,
  }: {
    appVersion: string;
    onSubmit?: () => void;
    updateAvailable: boolean;
  }) => {
    if (!appVersion || !onSubmit) return;
    handleSetModalType(ModalType.APP_VERSION);
    setModalData({
      appVersion,
      onSubmit,
      updateAvailable,
    });
  };

  const setGlobalModalData = (modal: ModalType, onSubmit: () => void) => {
    switch (modal) {
      case ModalType.SEND_LOGS: {
        handleSetModalType(ModalType.SEND_LOGS);
        setModalData({
          icon: <PaperPlaneIllustration />,
          description:
            'Thank you for your feedback. Our team will review the logs and get back to you shortly.',
          buttonLabel: 'OK',
          onSubmit,
        });
        break;
      }
      case ModalType.NEW_VERSION_DOWNLOADED: {
        handleSetModalType(ModalType.NEW_VERSION_DOWNLOADED);
        setModalData({
          icon: <MonitorIllustration />,
          description:
            'To complete the process, wait for the app to open automatically after you click ‘Exit and Relaunch’.',
          buttonLabel: 'Exit & Relaunch',
          onSubmit,
        });
        break;
      }
      default:
        break;
    }
  };

  const initEventListeners = () => {
    window.dsDesktop?.ipcRenderer.on(AppEventsEnum.ABOUT_US, showAboutUs);
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.SHOW_RESTART_APP_MODAL,
      showRestartAppModal
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.DIAGNOSTIC_CHECK,
      diagnosticCheck
    );
    window.dsDesktop?.ipcRenderer.on(AppEventsEnum.LOGS_SENT, () =>
      setGlobalModalData(ModalType.SEND_LOGS, () => closeModal())
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.UPDATE_AVAILABLE,
      (appData: any) => {
        if (appData.forceUpdate) setForceModal(true);
        showUpdateApp({
          appVersion: appData?.appVersion,
          onSubmit: () => window.dsDesktop?.app.updateApp(),
          updateAvailable: true,
        });
      }
    );
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.UPDATE_NOT_AVAILABLE,
      (appData: any) =>
        showUpdateApp({
          appVersion: appData?.appVersion,
          onSubmit: () => closeModal(),
          updateAvailable: false,
        })
    );
    window.dsDesktop?.ipcRenderer.on(AppEventsEnum.UPDATE_DOWNLOADED, () =>
      setGlobalModalData(ModalType.NEW_VERSION_DOWNLOADED, () =>
        window.dsDesktop?.app.relaunchApp()
      )
    );
  };

  useEffect(() => {
    initEventListeners();
  }, []);

  const getModalData = () => {
    switch (modalType) {
      case ModalType.APP_VERSION:
        return (
          <AppVersionModal
            appVersion={modalData?.appVersion}
            onSubmit={modalData?.onSubmit}
            showInfo={modalData?.showInfo}
            updateAvailable={modalData?.updateAvailable}
            postponeUpdate={!forceModal}
            onCloseModal={closeModal}
          />
        );
      case ModalType.NEW_VERSION_DOWNLOADED:
      case ModalType.SEND_LOGS:
        return (
          <div className="flex flex-col justify-center align-center pt-8 px-4 pb-2">
            {modalData?.icon}
            <div className="w-64 my-7 text-center">
              <p>{modalData.description}</p>
            </div>
            <Button
              label={modalData.buttonLabel}
              onClick={() => modalData.onSubmit()}
              className="min-w-[87px] bg-ds-primary py-2 rounded-md text-ds-natural-50 px-6"
            />
          </div>
        );
      case ModalType.DIAGNOSTICS_CHECK:
        return (
          <DiagnosticCheckScreen
            appData={modalData.appData}
            onCloseModal={closeModal}
          />
        );
      case ModalType.RESTART_APP:
        return <RestartAppModal onCloseModal={closeModal} />;
      default:
        return null;
    }
  };

  if (!modalType) return null;

  const {
    locked,
    onClose,
    closeOnlyFromCloseIcon,
    noBackground,
    removeCloseIcon,
    containerClass,
    isCenterPosition,
    title,
  } = modalConfigByType;
  return (
    <ModalWrapper
      locked={locked}
      onClose={onClose}
      closeOnlyFromCloseIcon={closeOnlyFromCloseIcon}
      isVisible={!!modalActions?.isModalOpen()}
      isCenterPosition={isCenterPosition}
      noBackground={noBackground}
      removeCloseIcon={removeCloseIcon}
      containerClass={containerClass}
      zIndex={APP_MODAL_Z_INDEX}
      title={title}
    >
      <div ref={modalRef}>{getModalData()}</div>
    </ModalWrapper>
  );
};

export default GlobalAppModals;
